import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import { handleError } from "cc-frontend/error-handling"
import groupChangeTitleAction from "cc-frontend/lib/actions/group/GROUP_CHANGE_TITLE"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import { isGroupMembershipIndividual } from "cc-frontend/utils/group"
import { filter } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./team-edit.module.css"

interface Signature {
  Args: {
    group: Group
    parentGroup: Group
  }
}

export default class GroupTeamEdit extends Component<Signature> {
  readonly styles = styles

  @service declare modalManager: ModalManager
  @service declare dialog: Dialog
  @service declare rpc: Rpc
  @service declare dispatcher: Dispatcher

  @tracked isDeleting = false

  get teamMembers() {
    return filter(
      get(get(this.args.group, "attributes"), "memberships"),
      isGroupMembershipIndividual
    )
  }

  @action setTitle(title: string) {
    return this.dispatcher.dispatchPreparedAction(
      groupChangeTitleAction({
        groupId: this.args.group.id,
        value: title,
        originalValue: this.args.group.attributes.title,
      })
    )
  }

  @action
  addMembers(group) {
    this.modalManager.openModal("group/add-members", {
      group: group,
      parentGroup: this.args.parentGroup,
      makeAdmin: false,
      initiallyOpened: true,
    })
  }

  @action
  deleteTeam(group: Group) {
    this.dialog
      .blank("dialogs/confirm-delete-team", {
        className: "dialog--create-annotation",
      })
      .then(() => {
        this.isDeleting = true
        this.rpc
          .group_delete_child_group({
            groupId: this.args.parentGroup.id,
            childGroupId: group.id,
          })
          .finally(() => {
            this.isDeleting = false
          })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::TeamEdit": typeof GroupTeamEdit
  }
}
